import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import type {Plan, PlanFeature} from '@Panels/premium-pricing-panel/premium-pricing-panel.types';
import {PlanCycle} from '@Panels/premium-pricing-panel/premium-pricing-panel.types';
import {isMonthly} from '@Libraries/change-subscription-plan-library';
import type {SubscriptionPlan} from '@Panels/change-plan-modal/change-plan-modal.types';

export function getPlanDescription(planLevel: USER_PREMIUM_LEVELS): string {
  if (planLevel === USER_PREMIUM_LEVELS.PREMIUM) {
    return window.i18next.t('pmwjs_get_more_powerful_design_tools_and_start_marketing_your_brand_like_a_pro');
  } else if (planLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS) {
    return window.i18next.t('pmwjs_all_about_growth_take_your_marketing_and_branding_to_new_heights');
  }

  return window.i18next.t('pmwjs_get_free_downloads_and_try_out_cool_design_features_no_strings_attached');
}

export function getPlanFeatures(planLevel: USER_PREMIUM_LEVELS): PlanFeature[] {
  if (planLevel === USER_PREMIUM_LEVELS.PREMIUM) {
    return [
      {icon: 'icon-check', description: window.i18next.t('pmwjs_everything_included_in_free')},
      {icon: 'icon-cloud-download', description: window.i18next.t('pmwjs_unlimited_downloads_no_watermark')},
      {icon: 'icon-clock-o', description: window.i18next.t('pmwjs_schedule_social_media_posts_and_emails')},
      {
        icon: 'icon-ai-image',
        description: window.i18next.t('pmwjs_ai_images_ai_writer_ai_subtitles_and_more_ai_tools'),
      },
      {
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_attract_crowds_with_x_event_pages_per_month_and_shareable_calendar', {numEvents: 5}),
      },
      {
        icon: 'icon-social-media',
        description: window.i18next.t('pmwjs_publish_on_x_social_media_accounts', {numAccounts: 3}),
      },
      {
        icon: 'icon-envelope',
        description: window.i18next.t('pmwjs_send_x_trackable_emails_per_month', {numEmails: 3000}),
      },
      {icon: 'icon-brands', description: window.i18next.t('pmwjs_your_own_brand_kit_and_custom_fonts')},
      {icon: 'icon-spellcheck', description: window.i18next.t('pmwjs_never_miss_a_typo_with_spellcheck')},
    ];
  } else if (planLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS) {
    return [
      {icon: 'icon-check', description: window.i18next.t('pmwjs_everything_included_in_premium')},
      {icon: 'icon-event', description: window.i18next.t('pmwjs_unlimited_event_pages')},
      {icon: 'icon-event', description: window.i18next.t('pmwjs_set_recurring_events')},
      {icon: 'icon-ai-image', description: window.i18next.t('pmwjs_more_custom_ai_images_highlighted')},
      {icon: 'icon-remove-bg', description: window.i18next.t('pmwjs_more_ai_bg_removals_highlighted')},
      {icon: 'icon-social-media', description: window.i18next.t('pmwjs_connect_unlimited_social_accounts')},
      {
        icon: 'icon-envelope',
        description: window.i18next.t('pmwjs_send_x_trackable_emails_per_month', {numEmails: 10000}),
      },
      {icon: 'icon-brands', description: window.i18next.t('pmwjs_unlimited_brand_kits')},
      {icon: 'icon-at', description: window.i18next.t('pmwjs_branded_email_addresses')},
    ];
  }

  return [
    {icon: 'icon-designs', description: window.i18next.t('pmwjs_1m_design_and_email_templates')},
    {icon: 'icon-cloud-download', description: window.i18next.t('pmwjs_free_social_media_downloads')},
    {icon: 'icon-social-media', description: window.i18next.t('pmwjs_publish_on_1_social_media_account')},
    {icon: 'icon-event', description: window.i18next.t('pmwjs_create_1_event_page_per_month')},
    {icon: 'icon-envelope', description: window.i18next.t('pmwjs_design_and_export_emails')},
    {icon: 'icon-resize', description: window.i18next.t('pmwjs_resize_your_designs_in_a_click')},
    {icon: 'icon-media', description: window.i18next.t('pmwjs_huge_library_of_stock_assets')},
    {icon: 'icon-animation', description: window.i18next.t('pmwjs_easy_slideshow_animations')},
  ];
}

export function getPlansToShow(plans: Plan[], selectedCycle: PlanCycle): Plan[] {
  const plansToShow: Plan[] = [
    {
      priceId: 0,
      name: window.i18next.t('pmwjs_free'),
      description: getPlanDescription(USER_PREMIUM_LEVELS.PAYG),
      price: 0,
      currencyCode: plans.length > 0 ? plans[0].currencyCode : 'USD',
      level: USER_PREMIUM_LEVELS.PAYG,
      features: getPlanFeatures(USER_PREMIUM_LEVELS.PAYG),
    },
  ];

  if (plans.length > 0) {
    plans.map((plan) => {
      if (plan.cycle === selectedCycle) {
        plansToShow.push(plan);
      }
    });
  }

  return plansToShow;
}

export function getOrderedPlans(plans: Plan[], isMobile: boolean): Plan[] {
  if (plans.length === 0) {
    return [];
  }

  let level1Plan: Plan | undefined;
  let level0Plan: Plan | undefined;
  const otherPlans: Plan[] = [];

  for (const plan of plans) {
    if (plan.level === USER_PREMIUM_LEVELS.PREMIUM) {
      level1Plan = plan;
    } else if (plan.level === USER_PREMIUM_LEVELS.PAYG) {
      level0Plan = plan;
    } else {
      otherPlans.push(plan);
    }
  }

  const orderedPlans: Plan[] = [];
  if (isMobile) {
    if (level1Plan) {
      orderedPlans.push(level1Plan);
    }
    orderedPlans.push(...otherPlans);
    if (level0Plan) {
      orderedPlans.push(level0Plan);
    }
  } else {
    if (level0Plan) {
      orderedPlans.push(level0Plan);
    }
    if (level1Plan) {
      orderedPlans.push(level1Plan);
    }
    orderedPlans.push(...otherPlans);
  }

  return orderedPlans;
}

export function mapSubscriptionPlanToPlan(subscriptionPlan: SubscriptionPlan): Plan {
  return {
    priceId: subscriptionPlan.price.id,
    name: subscriptionPlan.level === USER_PREMIUM_LEVELS.PREMIUM.valueOf() ? window.i18next.t('pmwjs_premium') : window.i18next.t('pmwjs_premium_plus'),
    description: getPlanDescription(subscriptionPlan.level),
    price: subscriptionPlan.tiers[0].displayPrice,
    currencyCode: subscriptionPlan.price.currencyCode,
    level: subscriptionPlan.level,
    features: getPlanFeatures(subscriptionPlan.level),
    cycle: isMonthly(subscriptionPlan.duration) ? PlanCycle.MONTHLY : PlanCycle.YEARLY,
  };
}

export function getPremiumMonthlyPrice(plans: Plan[]): number {
  return getMonthlyPlanPrice(plans, true);
}

export function getPremiumPlusMonthlyPrice(plans: Plan[]): number {
  return getMonthlyPlanPrice(plans, false);
}

function getMonthlyPlanPrice(plans: Plan[], isPremium: boolean): number {
  const level = isPremium ? USER_PREMIUM_LEVELS.PREMIUM : USER_PREMIUM_LEVELS.PREMIUM_PLUS;
  for (const plan of plans) {
    if (plan.level === level && plan.cycle === PlanCycle.MONTHLY) {
      return plan.price;
    }
  }

  return 0;
}
